import React, { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, X, Trash2, ShoppingCart, Loader } from "lucide-react";
import axios from "axios";
import { CartContext } from "./CartContext";
import { API_BASE_URL } from "../config";

const Wishlist = ({ isOpen, onClose }) => {
  const {
    wishlist,
    toggleWishlist,
    addToCart
  } = useContext(CartContext);

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError("");
      axios
        .get(`${API_BASE_URL}/all-data`)
        .then((res) => {
          setProducts(res.data || []);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error loading products for wishlist:", err);
          setError("Failed to load products.");
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  // Find all subproducts that are in the wishlist array
  const wishlistItems = [];
  products.forEach((prod) => {
    const subs = prod.subProducts || prod.subproducts || [];
    subs.forEach((sub) => {
      if (wishlist.includes(sub.sub_id)) {
        wishlistItems.push({
          ...sub,
          proId: prod.pro_id,
          categoryName: prod.name
        });
      }
    });
  });

  const handleMoveToCart = (item) => {
    const rateObj = (item.rateEntities || item.rateentities || [])[0] || {};
    const itemToAdd = {
      proId: item.proId,
      subId: item.sub_id,
      name: item.subName || "Cracker Item",
      quantity: 1,
      rate: rateObj.rate || 0,
      discount: rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0)
    };
    addToCart(itemToAdd);
    // Remove from wishlist after moving to cart (standard behavior)
    toggleWishlist(item.sub_id);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black z-[1040]"
          />

          {/* Slider Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed top-0 right-0 w-full sm:w-[450px] h-full bg-[#FAF8F5] shadow-2xl z-[1050] overflow-hidden flex flex-col justify-between font-sans border-l border-red-100"
          >
            {/* Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-red-950 to-red-900 text-white">
              <div className="flex items-center gap-2">
                <Heart size={20} className="text-yellow-400 fill-yellow-400" />
                <h2 className="text-lg font-black tracking-wide font-serif">My Festive Wishlist</h2>
                <span className="text-xs bg-red-850 px-2 py-0.5 rounded-full font-bold">
                  {wishlist.length} items
                </span>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-yellow-400 p-1 bg-white/10 hover:bg-white/20 rounded-lg transition"
                aria-label="Close wishlist"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Items list */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {isLoading ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-2 py-20 text-gray-500">
                  <Loader className="animate-spin text-red-900" size={24} />
                  <p className="text-xs font-bold">Loading wishlist details...</p>
                </div>
              ) : error ? (
                <p className="text-center py-20 text-xs font-bold text-red-600">⚠️ {error}</p>
              ) : wishlistItems.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-20">
                  <span className="text-5xl">❤️</span>
                  <p className="text-gray-500 font-bold text-sm">Your wishlist is empty.</p>
                  <button
                    onClick={onClose}
                    className="px-6 py-2.5 bg-red-900 text-white font-black text-xs uppercase tracking-wider rounded-xl hover:bg-red-800 transition"
                  >
                    Explore Catalogs
                  </button>
                </div>
              ) : (
                <div className="space-y-3.5">
                  {wishlistItems.map((item) => {
                    const rateObj = (item.rateEntities || item.rateentities || [])[0] || {};
                    const rate = rateObj.rate || 0;
                    const discount = rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0);
                    const discountRate = Math.round(rate * (1 - discount / 100));

                    return (
                      <div
                        key={item.sub_id}
                        className="p-3 bg-white border border-gray-150 rounded-2xl flex items-center justify-between gap-4 hover:shadow-xs transition"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {item.childimg && (
                            <img
                              src={item.childimg}
                              alt={item.subName}
                              className="w-12 h-12 object-cover rounded-xl border border-gray-100 shrink-0"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="text-[9px] uppercase font-black text-[#500707] tracking-wider block">
                              {item.categoryName}
                            </span>
                            <h4 className="font-bold text-gray-800 text-sm truncate capitalize">
                              {item.subName}
                            </h4>
                            <div className="flex items-center gap-2 text-xs mt-0.5">
                              <span className="font-black text-green-700">₹{discountRate}</span>
                              {discount > 0 && (
                                <span className="line-through text-gray-400">₹{rate}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleMoveToCart(item)}
                            className="bg-yellow-400 hover:bg-yellow-500 text-red-950 px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition shadow-sm hover:shadow flex items-center gap-1"
                            title="Add to Cart"
                          >
                            <ShoppingCart size={12} />
                            <span>Add</span>
                          </button>
                          <button
                            onClick={() => toggleWishlist(item.sub_id)}
                            className="text-gray-400 hover:text-red-650 p-2 hover:bg-red-50 rounded-xl transition"
                            title="Remove from wishlist"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {wishlistItems.length > 0 && (
              <div className="p-5 border-t border-gray-100 bg-white">
                <button
                  onClick={() => {
                    wishlistItems.forEach(item => handleMoveToCart(item));
                    onClose();
                  }}
                  className="w-full bg-[#500707] hover:bg-[#6f0d0d] text-white py-3 rounded-2xl font-black text-xs uppercase tracking-wider transition shadow-md flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={15} />
                  <span>Move All To Cart</span>
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Wishlist;
